import type React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { EditorialHeader, PageError } from './layout';
import { ArticleSectionBySectionSkeleton } from './ArticleSectionBySectionSkeleton';
import { Button } from './ui/button';
import { articleService } from '../utils/services/articleService.js';
import { outlineService } from '../utils/services/outlineService.js';
import { useGenerateSection } from '../features/article/api/articleQueries';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Input } from './ui/input';
import { ArticleSectionEditor, type ArticleSection } from './ArticleSectionEditor';
import { ArticleReferences } from './ArticleReferences';
import { StyleGuideSelector } from './StyleGuideSelector';
import { DeletionConfirmationDialog } from './ui/deletion-confirmation-dialog';
import { ArticleHeaderImage } from './ArticleHeaderImage';
import { ImageGenerationWizard } from './ImageGenerationWizard';
import { useArticleHeaderImage } from '../hooks/useArticleHeaderImage';
import { toast } from 'sonner';
import {
  Loader2, AlertCircle, FileText,
  RefreshCw, Edit, SkipForward, Download, ArrowLeft, Trash2,
  Share2, Sparkles, Square
} from 'lucide-react';
import { useGeneratePost, useStyleGuides } from '../features/article/api/postQueries';
import {
  useGenerateImagePrompt,
  useGenerateImage,
  type AspectRatio,
} from '../features/article/api/imageQueries';
import type { HeaderImageData } from '../utils/headerImageStorage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
} from './ui/sidebar';
import { Separator } from './ui/separator';
import { NavigationGuard } from './NavigationGuard';

interface SectionState {
  index: number;
  title: string;
  status: 'pending' | 'generating' | 'complete' | 'error';
  content: string;
  wordCount: number;
  canGenerate: boolean;
  needsReview?: boolean;
  errorMessage?: string;
  progress?: number;
  metadata?: {
    sectionDescription?: string;
    targetWordCount?: number;
  };
}

interface OutlineData {
  title: string;
  sections: Array<{
    title: string;
    description: string;
    word_count: number;
  }>;
}

// Utility functions
const calculateWordCount = (text: string): number =>
  text.split(/\s+/).filter(w => w.length > 0).length;

const hasContent = (section: { content?: string; editedContent?: string }): boolean =>
  Boolean(section.content?.length || section.editedContent?.length);

const getNextGeneratableIndex = (sections: SectionState[]): number => {
  const firstIncomplete = sections.findIndex(s => !s.content?.length);
  return firstIncomplete === -1 ? 0 : firstIncomplete;
};

const getNextSectionUpdate = (sectionIndex: number, sectionsLength: number): Array<{ index: number; updates: Partial<SectionState> }> =>
  sectionIndex + 1 < sectionsLength ? [{
    index: sectionIndex + 1,
    updates: { canGenerate: true }
  }] : [];

export const ArticleSectionBySection: React.FC = () => {
  const navigate = useNavigate();
  const { id: articleIdParam } = useParams<{ id?: string }>();
  const { getToken } = useAuth();

  // React Query mutation for section generation with automatic retry
  const generateSectionMutation = useGenerateSection();

  const [sections, setSections] = useState<SectionState[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingSectionIndex, setGeneratingSectionIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [outline, setOutline] = useState<OutlineData | null>(null);
  const [isLoadingArticle, setIsLoadingArticle] = useState(false);
  const [articleTitle, setArticleTitle] = useState<string>('');

  const [selectedStyleGuide, setSelectedStyleGuide] = useState<string>('linkedin_style_guidelines');
  const [streamingContent, setStreamingContent] = useState<Record<number, string>>({});
  const [loadedBrief, setLoadedBrief] = useState<LoadedBrief | null>(null);
  const [briefId, setBriefId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Generate All Sections State
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [generateAllProgress, setGenerateAllProgress] = useState<{ current: number; total: number } | null>(null);
  const cancelGenerateAllRef = useRef(false);

  // Post Generation State
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [generatedPost, setGeneratedPost] = useState<string | null>(null);
  const [customPostPrompt, setCustomPostPrompt] = useState('');
  const [selectedStyleGuideId, setSelectedStyleGuideId] = useState<string>('');

  // Image Generation Wizard State
  const [imageWizardOpen, setImageWizardOpen] = useState(false);
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  const [imageGenerationError, setImageGenerationError] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Store last generation config for retry functionality
  const [lastGenerationConfig, setLastGenerationConfig] = useState<{
    prompt: string;
    style: string;
    aspectRatio: AspectRatio;
  } | null>(null);

  const generatePostMutation = useGeneratePost();
  const { data: styleGuidesData, isLoading: styleGuidesLoading } = useStyleGuides();
  const activeStyleGuides = (styleGuidesData?.styleGuides || []).filter(sg => sg.isActive);

  // Image generation mutations for Easy mode
  const generateImagePromptMutation = useGenerateImagePrompt();
  const generateImageMutation = useGenerateImage();
  
  // Header image hook - use articleId or articleIdParam as fallback
  const { imageData: headerImageData, isLoading: headerImageLoading, saveImage: saveHeaderImage } = useArticleHeaderImage(articleId || articleIdParam || '');

  const handleGeneratePost = () => {
    if (!articleId || !selectedStyleGuideId) {
      toast.error('Configuration incomplete', {
        description: 'Please select a style guide before generating.'
      });
      return;
    }

    generatePostMutation.mutate(
      {
        articleId,
        styleGuideId: selectedStyleGuideId,
        customPrompt: customPostPrompt
      },
      {
        onSuccess: (data) => {
          setGeneratedPost(data.post);
        },
        onError: (error) => {
          toast.error(`Failed to generate post: ${error.message}`);
        }
      }
    );
  };

  /**
   * Handle Easy mode image generation
   * Called when user clicks "Generate Image" in Easy mode - dialog closes, we handle generation here
   */
  const handleEasyModeImageStart = async (config: { style: string; aspectRatio: AspectRatio }) => {
    const currentArticleId = articleId || articleIdParam || '';
    if (!currentArticleId) return;

    setIsImageGenerating(true);
    setImageGenerationError(null);

    try {
      // Step 1: Generate prompt
      const promptResult = await generateImagePromptMutation.mutateAsync({
        articleId: currentArticleId,
        style: config.style,
      });

      // Store config for potential retry
      const fullConfig = {
        prompt: promptResult.prompt,
        style: config.style,
        aspectRatio: config.aspectRatio,
      };
      setLastGenerationConfig(fullConfig);

      // Step 2: Generate image
      const imageResult = await generateImageMutation.mutateAsync({
        articleId: currentArticleId,
        prompt: promptResult.prompt,
        aspectRatio: config.aspectRatio,
      });

      // Step 3: Save to storage
      const headerImageData: HeaderImageData = {
        imageUrl: imageResult.imageUrl,
        aspectRatio: config.aspectRatio,
        prompt: promptResult.prompt,
        style: config.style,
        generatedAt: Date.now(),
      };
      saveHeaderImage(headerImageData);
    } catch (error) {
      console.error('Easy mode image generation failed:', error);
      setImageGenerationError(error instanceof Error ? error.message : 'Failed to generate image');
    } finally {
      setIsImageGenerating(false);
    }
  };

  /**
   * Handle Advanced mode image generation
   * Called when user clicks "Generate" in Advanced mode after editing prompt - dialog closes, we handle generation here
   */
  const handleAdvancedModeImageStart = async (config: { prompt: string; style: string; aspectRatio: AspectRatio }) => {
    const currentArticleId = articleId || articleIdParam || '';
    if (!currentArticleId) return;

    setIsImageGenerating(true);
    setImageGenerationError(null);
    setLastGenerationConfig(config);

    try {
      // Generate image with user-provided prompt
      const imageResult = await generateImageMutation.mutateAsync({
        articleId: currentArticleId,
        prompt: config.prompt,
        aspectRatio: config.aspectRatio,
      });

      // Save to storage
      const headerImageData: HeaderImageData = {
        imageUrl: imageResult.imageUrl,
        aspectRatio: config.aspectRatio,
        prompt: config.prompt,
        style: config.style,
        generatedAt: Date.now(),
      };
      saveHeaderImage(headerImageData);
    } catch (error) {
      console.error('Advanced mode image generation failed:', error);
      setImageGenerationError(error instanceof Error ? error.message : 'Failed to generate image');
    } finally {
      setIsImageGenerating(false);
    }
  };

  /**
   * Handle retry of failed image generation
   */
  const handleRetryImageGeneration = () => {
    if (lastGenerationConfig) {
      handleAdvancedModeImageStart(lastGenerationConfig);
    }
  };

  /**
   * Handle opening the image wizard
   * @param forRegeneration - True if regenerating an existing image (forces Advanced mode)
   */
  const handleOpenImageWizard = (forRegeneration: boolean = false) => {
    setIsRegenerating(forRegeneration);
    setImageWizardOpen(true);
  };

  /**
   * Handle closing the image wizard
   * Clears the isRegenerating flag to prevent state leakage
   */
  const handleImageWizardClose = (open: boolean) => {
    setImageWizardOpen(open);
    if (!open) {
      setIsRegenerating(false); // Clear flag when wizard closes
    }
  };

  const eventSourceRef = useRef<any>(null);
  const titleSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const loadingRef = useRef<boolean>(false); // Synchronous flag to prevent duplicate API calls

  // Type definitions for better type safety
  interface LoadedBrief {
    id: string;
    urls: string[];
    title: string;
  }

  // Section update helpers
  const updateSection = useCallback((index: number, updates: Partial<SectionState>) => {
    setSections(prev => prev.map((s, i) =>
      i === index ? { ...s, ...updates } : s
    ));
  }, []);

  // Helper to apply updates to a section (extracted to reduce nesting)
  const applyUpdate = useCallback((section: SectionState, updates: Array<{ index: number; updates: Partial<SectionState> }>) => {
    const update = updates.find(u => u.index === section.index);
    return update ? { ...section, ...update.updates } : section;
  }, []);

  const updateMultipleSections = useCallback((updates: Array<{ index: number; updates: Partial<SectionState> }>) => {
    setSections(prev => prev.map(s => applyUpdate(s, updates)));
  }, [applyUpdate]);

  // Article save payload construction helper
  const buildSavePayload = useCallback((id: string | null, sections: SectionState[]) => {
    const fullContent = sections
      .filter(s => s.content)
      .map(s => `## ${s.title}\n\n${s.content}`)
      .join('\n\n');

    const articleSections = sections.map(s => ({
      index: s.index,
      title: s.title,
      content: s.content,
      wordCount: s.wordCount,
      metadata: s.metadata
    }));

    console.log('💾 DEBUG: buildSavePayload called', {
      totalSections: sections.length,
      sectionsWithContent: sections.filter(s => s.content).length,
      sectionsInPayload: articleSections.length,
      sectionDetails: articleSections.map(s => ({
        index: s.index,
        title: s.title,
        hasContent: !!s.content,
        contentLength: s.content?.length || 0
      }))
    });

    const lastGeneratedIndex = sections.reduce((maxIndex, section) => {
      return hasContent(section) ? section.index : maxIndex;
    }, -1);

    const payload: any = {
      title: articleTitle || outline?.title,
      fullContent,
      sections: articleSections,
      references: loadedBrief
        ? loadedBrief.urls.map((url: string) => ({ url }))
        : [],
      outline,
      lastGeneratedIndex,
      ...(briefId && { articleBriefId: briefId })
    };

    if (id) {
      payload.id = id;
    }

    return payload;
  }, [articleTitle, outline, loadedBrief, briefId]);

  // Helper to load article sections from existing article
  const loadArticleSections = useCallback(async (article: any) => {
    console.log('🔍 DEBUG: Loading article sections', {
      articleId: article.id,
      hasSections: !!article.sections,
      sectionCount: article.sections?.length,
      sections: article.sections?.map((s: any) => ({
        number: s.sectionNumber,
        title: s.title,
        hasContent: !!s.content,
        contentLength: s.content?.length || 0
      }))
    });

    setArticleId(article.id);
    if (article.articleBriefId) {
      setBriefId(article.articleBriefId);
    }

    // Check if outline is missing but outlineId exists
    let outlineData = article.outline;

    // Always try to fetch outline if it's missing (use article.id as fallback)
    if (!outlineData) {
      // Use articleBriefId, outlineId, or article.id as they are often the same
      const briefId = article.articleBriefId || article.outlineId || article.id;
      try {
        const { outline: fetchedOutline, brief } = await outlineService.loadBriefAndOutline(briefId, getToken);
        outlineData = fetchedOutline;
        if (brief) {
          setLoadedBrief(brief);
        }
      } catch (error) {
        console.error('Failed to fetch outline:', error);
      }
    }

    setOutline(outlineData);
    setArticleTitle(article.title || outlineData?.title || '');
    if (article.metadata?.styleGuide) {
      setSelectedStyleGuide(article.metadata.styleGuide);
    }

    // Check if article has saved sections with actual content
    // Always create sections from outline first to ensure all sections exist
    // Then overlay database sections on top where they exist
    const outlineSections = outlineData?.sections || [];
    const dbSections = article.sections || [];

    console.log('🔍 DEBUG: Merging sections', {
      dbSectionCount: dbSections.length,
      outlineSectionCount: outlineSections.length
    });

    // Create a map of DB sections by their index (converted from 1-based to 0-based)
    const dbSectionMap = new Map<number, any>(
      dbSections.map((dbSection: any) => [
        dbSection.sectionNumber - 1, // DB is 1-based, UI is 0-based
        dbSection
      ])
    );

    // Start with outline sections, then overlay DB content where it exists
    const loadedSections: SectionState[] = outlineSections.map((section: any, index: number) => {
      const dbSection: any = dbSectionMap.get(index);

      if (dbSection && dbSection.content) {
        // Section exists in DB with content - use it
        return {
          index,
          title: dbSection.title || section.title,
          status: 'complete' as const,
          content: dbSection.content,
          wordCount: dbSection.wordCount || calculateWordCount(dbSection.content),
          canGenerate: false,
          metadata: {
            sectionDescription: section.description,
            targetWordCount: section.word_count
          }
        };
      } else {
        // No DB section or empty content - create from outline
        return {
          index,
          title: section.title,
          status: 'pending' as const,
          content: '',
          wordCount: 0,
          canGenerate: false,
          metadata: {
            sectionDescription: section.description,
            targetWordCount: section.word_count
          }
        };
      }
    });

    const nextIndex = getNextGeneratableIndex(loadedSections);
    loadedSections.forEach((section, i) => {
      // Allow any empty section to generate, not just the next sequential one
      section.canGenerate = section.content.length === 0 || i === nextIndex;
    });

    console.log('🔍 DEBUG: Final loaded sections', {
      count: loadedSections.length,
      sections: loadedSections.map(s => ({
        index: s.index,
        title: s.title,
        hasContent: !!s.content,
        canGenerate: s.canGenerate
      }))
    });

    setSections(loadedSections);
  }, [getToken]);

  // Helper to create article from outline
  const createArticleFromOutline = useCallback(async (outlineData: any, brief: any, briefIdParam: string) => {
    // Create sections from outline BEFORE any state updates
    const sectionsFromOutline: SectionState[] = outlineData.sections.map(
      (section: OutlineData['sections'][0], index: number) => ({
        index,
        title: section.title,
        status: 'pending' as const,
        content: '',
        wordCount: 0,
        canGenerate: index === 0,
        metadata: {
          sectionDescription: section.description,
          targetWordCount: section.word_count
        }
      })
    );

    setSelectedStyleGuide((current) => current || 'linkedin_style_guidelines');

    // Now update state AFTER creating the sections
    setBriefId(briefIdParam);
    setLoadedBrief(brief);
    setOutline(outlineData);
    setArticleTitle(outlineData.title || brief.title || '');
    setSections(sectionsFromOutline);

    // Create article payload using the sections we created
    const fullContent = sectionsFromOutline
      .filter(s => s.content)
      .map(s => `## ${s.title}\n\n${s.content}`)
      .join('\n\n');

    const articleSections = sectionsFromOutline.map(s => ({
      index: s.index,
      title: s.title,
      content: s.content,
      wordCount: s.wordCount,
      metadata: s.metadata
    }));

    const savePayload: any = {
      title: outlineData.title || brief.title || '',
      fullContent,
      sections: articleSections,
      references: brief.urls ? brief.urls.map((url: string) => ({ url })) : [],
      outline: outlineData,
      lastGeneratedIndex: -1,
      articleBriefId: briefIdParam,  // Use param directly, not state
      metadata: {
        styleGuide: selectedStyleGuide
      }
    };

    try {
      const newArticle = await articleService.create(savePayload, getToken);
      console.log('✅ [ARTICLE] Article created successfully', {
        articleId: newArticle.id,
        briefId: briefIdParam
      });
      setArticleId(newArticle.id);
      return newArticle;
    } catch (error) {
      console.error('Failed to create article:', error);
      throw error;
    }
  }, [selectedStyleGuide, getToken]);

  // Main loading logic with three clear phases
  const loadArticleOrCreateFromOutline = useCallback(async (briefIdParam: string) => {
    // Prevent double-loading with synchronous ref check (handles React StrictMode)
    if (loadingRef.current) {
      console.log('⏸️ [ARTICLE] Already loading (ref check), skipping duplicate call');
      return;
    }

    // Set both synchronous flag and async state
    loadingRef.current = true;
    setIsLoadingArticle(true);
    setError(null);

    try {
      // PHASE 1: Check for existing article
      const articlesResponse = await articleService.list({ briefId: briefIdParam }, getToken);

      if (articlesResponse.count > 0) {
        const article = articlesResponse.data[0];
        await loadArticleSections(article);
        return;
      }

      // PHASE 2: No article exists, check for outline
      const outlineExists = await outlineService.checkExists(briefIdParam);

      if (outlineExists) {
        // PHASE 3a: Outline exists - create article automatically
        toast.info('Setting up your writing workspace...', {
          description: 'Getting your article ready from your structure.'
        });

        const { outline: outlineData, brief } = await outlineService.loadBriefAndOutline(briefIdParam, getToken);
        await createArticleFromOutline(outlineData, brief, briefIdParam);

        toast.success('Workspace ready!', {
          description: 'Time to bring your structure to life.'
        });
      } else {
        // PHASE 3b: No outline exists - show error
        setError('no_outline');
      }
    } catch (err) {
      console.error('Error loading article:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error('Failed to load article', {
        description: errorMessage
      });
    } finally {
      loadingRef.current = false;
      setIsLoadingArticle(false);
    }
  }, [loadArticleSections, createArticleFromOutline, getToken]);

  // Load article on mount or when articleIdParam changes
  useEffect(() => {
    if (!articleIdParam) {
      return;
    }

    // Use a flag to prevent stale updates after unmount (React StrictMode)
    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        await loadArticleOrCreateFromOutline(articleIdParam);
      }
    };

    loadData();

    // Cleanup function prevents updates after unmount
    return () => {
      isMounted = false;
    };
  }, [articleIdParam, loadArticleOrCreateFromOutline]);





  // Auto-save after section completion or title change
  const autoSave = useCallback(async () => {
    if (!outline || sections.length === 0 || !articleId) return;

    console.log('💾 DEBUG: autoSave triggered', {
      sectionCount: sections.length,
      sectionsWithContent: sections.filter(s => s.content).length,
      sectionDetails: sections.map(s => ({
        index: s.index,
        title: s.title,
        hasContent: !!s.content,
        contentLength: s.content?.length || 0
      }))
    });

    setIsSaving(true);
    try {
      const payload = buildSavePayload(articleId, sections);
      console.log('💾 DEBUG: Calling articleService.save with payload containing', payload.sections.length, 'sections');

      const article = await articleService.save(payload, getToken);

      console.log('💾 DEBUG: Save response received', {
        returnedSectionCount: article.sections?.length,
        returnedSections: article.sections?.map((s: any) => ({
          number: s.sectionNumber,
          title: s.title,
          hasContent: !!s.content,
          contentLength: s.content?.length || 0
        }))
      });

      if (article.id) {
        setArticleId(article.id);
      }
    } catch (err) {
      console.error('💾 DEBUG: Auto-save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [outline, sections, articleId, buildSavePayload, getToken]);

  // Debounced auto-save when title or sections change with proper cleanup
  useEffect(() => {
    if (articleTitle && articleId) {
      if (titleSaveTimeoutRef.current) {
        clearTimeout(titleSaveTimeoutRef.current);
      }

      titleSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 1000);
    }

    // Cleanup function always clears timeout
    return () => {
      if (titleSaveTimeoutRef.current) {
        clearTimeout(titleSaveTimeoutRef.current);
        titleSaveTimeoutRef.current = null;
      }
    };
  }, [articleTitle, articleId, autoSave]);

  const executeSectionGeneration = useCallback((
    sectionIndex: number,
    callbacks?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    if (!articleId) {
      const error = new Error('Article ID is required for section generation');
      callbacks?.onError?.(error);
      return;
    }

    setStreamingContent(prev => {
      const { [sectionIndex]: _, ...rest } = prev;
      return rest;
    });

    setIsGenerating(true);
    setGeneratingSectionIndex(sectionIndex);
    setError(null);
    updateSection(sectionIndex, { status: 'generating' as const, errorMessage: undefined });

    generateSectionMutation.mutate({
      articleId,
      sectionIndex,
      styleGuide: selectedStyleGuide,
      onProgress: (chunk: string) => {
        setStreamingContent(prev => ({
          ...prev,
          [sectionIndex]: (prev[sectionIndex] || '') + chunk
        }));
      },
      onComplete: (content: string) => {
        console.log('✅ [GENERATE] Section generation complete', { sectionIndex, contentLength: content.length });

        const wordCount = calculateWordCount(content);
        updateSection(sectionIndex, {
          status: 'complete' as const,
          content,
          wordCount,
          canGenerate: false,
          errorMessage: undefined
        });

        setStreamingContent(prev => {
          const { [sectionIndex]: _, ...rest } = prev;
          return rest;
        });

        const nextUpdates = getNextSectionUpdate(sectionIndex, sections.length);
        if (nextUpdates.length > 0) {
          updateMultipleSections(nextUpdates);
        }

        setIsGenerating(false);
        setGeneratingSectionIndex(null);

        autoSave().catch(err => {
          console.error('Auto-save after generation failed:', err);
        });

        callbacks?.onSuccess?.();
      }
    }, {
      onError: (error: Error) => {
        console.error('❌ [GENERATE] Section generation failed', { sectionIndex, error: error.message });

        updateSection(sectionIndex, {
          status: 'error' as const,
          errorMessage: error.message || 'Failed to generate section'
        });

        setStreamingContent(prev => {
          const { [sectionIndex]: _, ...rest } = prev;
          return rest;
        });

        setIsGenerating(false);
        setGeneratingSectionIndex(null);
        setError(error.message);

        callbacks?.onError?.(error);
      }
    });
  }, [articleId, selectedStyleGuide, generateSectionMutation, updateSection, updateMultipleSections, sections.length, autoSave]);

  const handleGenerateSection = useCallback((sectionIndex: number, isRegeneration: boolean = false) => {
    const section = sections[sectionIndex];

    if (!section || isGenerating) return;

    console.log('🚀 [GENERATE] Starting section generation', {
      sectionIndex, articleId, briefId, sectionTitle: section.title, isRegeneration
    });

    if (!isRegeneration && !section.canGenerate) return;

    if (!loadedBrief) {
      setError('No article project loaded');
      return;
    }

    if (!articleId) {
      console.error('❌ [GENERATE] Article ID is required for section generation');
      updateSection(sectionIndex, { status: 'error' as const, errorMessage: 'Article ID is required for section generation' });
      return;
    }

    executeSectionGeneration(sectionIndex);
  }, [sections, isGenerating, articleId, briefId, loadedBrief, updateSection, executeSectionGeneration]);

  const generateSectionAsync = useCallback((sectionIndex: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!articleId || !loadedBrief) {
        reject(new Error('Article not ready'));
        return;
      }
      executeSectionGeneration(sectionIndex, { onSuccess: resolve, onError: reject });
    });
  }, [articleId, loadedBrief, executeSectionGeneration]);

  const handleRetry = useCallback((sectionIndex: number) => {
    handleGenerateSection(sectionIndex);
  }, [handleGenerateSection]);

  // Manual entry - redirect to brief detail for editing
  const handleManualEntry = () => {
    if (loadedBrief) {
      navigate(`/brief/${loadedBrief.id}`);
    }
  };

  // Skip section
  const handleSkip = (sectionIndex: number) => {
    updateMultipleSections([
      {
        index: sectionIndex,
        updates: {
          status: 'pending' as const,
          content: '[SKIPPED - TODO]',
          errorMessage: undefined
        }
      },
      ...getNextSectionUpdate(sectionIndex, sections.length)
    ]);
  };

  const handleGenerateAll = useCallback(async () => {
    const pendingSections = sections.filter(s => !s.content || s.content.length === 0);
    
    if (pendingSections.length === 0) {
      toast.info('All sections already have content');
      return;
    }

    if (!articleId || !loadedBrief) {
      toast.error('Article not ready for generation');
      return;
    }

    setIsGeneratingAll(true);
    cancelGenerateAllRef.current = false;
    setGenerateAllProgress({ current: 0, total: pendingSections.length });

    toast.info(`Starting generation of ${pendingSections.length} sections...`);

    let completedCount = 0;
    let errorCount = 0;

    for (const section of pendingSections) {
      if (cancelGenerateAllRef.current) {
        toast.info(`Generation cancelled after ${completedCount} sections`);
        break;
      }

      setGenerateAllProgress({ current: completedCount + 1, total: pendingSections.length });

      try {
        await generateSectionAsync(section.index);
        completedCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to generate section ${section.index}:`, error);
        
        if (errorCount >= 2) {
          toast.error('Multiple failures detected. Stopping generation.');
          break;
        }
      }
    }

    setIsGeneratingAll(false);
    setGenerateAllProgress(null);

    if (completedCount > 0 && errorCount === 0) {
      toast.success(`Successfully generated ${completedCount} sections!`);
    } else if (completedCount > 0) {
      toast.warning(`Generated ${completedCount} sections with ${errorCount} errors`);
    }
  }, [sections, articleId, loadedBrief, generateSectionAsync]);

  const handleCancelGenerateAll = useCallback(() => {
    cancelGenerateAllRef.current = true;
    toast.info('Cancelling after current section completes...');
  }, []);

  if (isLoadingArticle) {
    return <ArticleSectionBySectionSkeleton />;
  }

  // Error state for missing brief
  if (!articleIdParam && !loadedBrief) {
    return (
      <PageError
        title="No article project loaded"
        message="Please select an article from your dashboard to continue"
        onBack={() => navigate('/dashboard')}
      />
    );
  }

  const handleBackToOutline = () => {
    if (loadedBrief) {
      navigate(`/outline/${loadedBrief.id}`);
      return;
    }
    navigate('/dashboard');
  };

  const handleDelete = async () => {
    if (!articleId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!articleId) return;

    try {
      await articleService.delete(articleId, getToken);
      toast.success('Article deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete article:', error);
      toast.error('Failed to delete article. Please try again.');
    }
  };

  // Scroll to section when sidebar item is clicked
  const scrollToSection = (index: number) => {
    const element = sectionRefs.current.get(index);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-full w-full">
        <Sidebar side="left" className="border-r">
          <SidebarHeader>
            <div className="flex items-center gap-2 px-4 py-2">
              <SidebarTrigger />
              <h2 className="text-lg font-semibold">Writing Tools</h2>
              <div className="ml-auto flex gap-1">
                <Button variant="ghost" size="icon" title="Create Post" onClick={() => setPostDialogOpen(true)}>
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <SidebarSeparator />
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Writing Style</SidebarGroupLabel>
              <SidebarGroupContent className="px-4">
                <StyleGuideSelector value={selectedStyleGuide} onChange={setSelectedStyleGuide} />
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Your Structure</SidebarGroupLabel>
              <SidebarGroupContent className="px-4">
                {outline?.sections ? (
                  <div className="space-y-2">
                    {outline.sections.map((section, index) => {
                      const sectionState = sections[index];
                      return (
                        <button
                          type="button"
                          // biome-ignore lint/suspicious/noArrayIndexKey: outline sections have no unique ID; order is stable
                          key={index}
                          className="flex items-center justify-between text-sm cursor-pointer hover:bg-accent/50 rounded-md px-2 py-1.5 -mx-2 transition-colors w-full text-left"
                          onClick={() => scrollToSection(index)}
                        >
                          <span className="truncate flex-1 mr-2">{section.title}</span>
                          <div className="flex items-center gap-1">
                            {sectionState && (
                              <>
                                <Badge
                                  variant={
                                    sectionState.status === 'complete' ? 'default' :
                                      sectionState.status === 'generating' ? 'secondary' :
                                        sectionState.status === 'error' ? 'destructive' : 'outline'
                                  }
                                  className="text-xs"
                                >
                                  {sectionState.status === 'generating' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                  {sectionState.wordCount || 0}
                                </Badge>
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <SidebarSeparator className="my-3" />
                    <div className="flex items-center justify-between text-sm pt-1">
                      <span className="font-medium text-muted-foreground">Total Words:</span>
                      <Badge variant="secondary" className="font-mono">
                        {sections.reduce((total, section) => total + section.wordCount, 0).toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No structure loaded</div>
                )}
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <SidebarInset className="flex-1">
          {/* Dialogs for Post and Image Generation */}
          <Dialog open={postDialogOpen} onOpenChange={(open) => {
            setPostDialogOpen(open);
            if (!open) {
              // Reset state when closing
              setGeneratedPost(null);
              setCustomPostPrompt('');
              setSelectedStyleGuideId('');
            }
          }}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Generate LinkedIn Post</DialogTitle>
                <DialogDescription>
                  {generatedPost
                    ? 'Your post has been generated successfully.'
                    : 'Select a writing style and optionally add custom instructions.'}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {!generatedPost ? (
                  <>
                    {/* Style Guide Selection - REQUIRED */}
                    <div className="grid gap-2">
                      <Label htmlFor="style-guide">Writing Style *</Label>
                      <Select
                        value={selectedStyleGuideId}
                        onValueChange={setSelectedStyleGuideId}
                        disabled={styleGuidesLoading}
                      >
                        <SelectTrigger id="style-guide">
                          <SelectValue placeholder="Select a writing style..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activeStyleGuides.map((sg) => (
                            <SelectItem key={sg.id} value={sg.id}>
                              {sg.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {activeStyleGuides.length === 0 && !styleGuidesLoading && (
                        <p className="text-xs text-muted-foreground">
                          No active writing styles available. Contact an admin to create style guides.
                        </p>
                      )}
                    </div>

                    {/* Custom Instructions - Optional */}
                    <div className="grid gap-2">
                      <Label htmlFor="post-prompt">Custom Instructions (Optional)</Label>
                      <Textarea
                        id="post-prompt"
                        placeholder="E.g., Focus on the second section, make it punchy..."
                        value={customPostPrompt}
                        onChange={(e) => setCustomPostPrompt(e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <div className="grid gap-2">
                    <Label>Generated Post</Label>
                    <div className="bg-muted p-4 rounded-md whitespace-pre-wrap max-h-[300px] overflow-y-auto text-sm">
                      {generatedPost}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {generatedPost ? (
                  <>
                    <Button variant="outline" onClick={() => {
                      setGeneratedPost(null);
                      setCustomPostPrompt('');
                      // Keep selectedStyleGuideId to allow regeneration with same style
                    }}>Generate New</Button>
                    <Button onClick={() => {
                      navigator.clipboard.writeText(generatedPost);
                      toast.success('Post copied to clipboard');
                    }}>Copy to Clipboard</Button>
                  </>
                ) : (
                  <Button
                    onClick={handleGeneratePost}
                    disabled={
                      generatePostMutation.isPending ||
                      !selectedStyleGuideId ||
                      styleGuidesLoading
                    }
                  >
                    {generatePostMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Generate Post
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Image Generation Wizard */}
          <ImageGenerationWizard
            articleId={articleId || articleIdParam || ''}
            articleTitle={articleTitle || outline?.title || 'Article'}
            open={imageWizardOpen}
            onOpenChange={handleImageWizardClose}
            forceAdvancedMode={isRegenerating}
            onEasyModeStart={handleEasyModeImageStart}
            onAdvancedModeStart={handleAdvancedModeImageStart}
          />
          {/* Sidebar trigger row */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-ochre/10 bg-paper-warm/30">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-6" />
            <span className="text-sm text-muted-warm">Toggle Tools</span>
          </div>
          {/* Editorial header */}
          <EditorialHeader
            title="Write Your Article"
            description="Bring your structure to life, section by section"
          />

          {/* Navigation Buttons */}
          <div className="border-b px-4 py-2">
            <div className="container mx-auto flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleBackToOutline}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Structure
                </Button>
                {articleId && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Article
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {articleId && sections.some(s => !s.content || s.content.length === 0) && (
                  isGeneratingAll ? (
                    <Button
                      variant="outline"
                      onClick={handleCancelGenerateAll}
                      className="gap-2"
                    >
                      <Square className="h-4 w-4" />
                      Cancel ({generateAllProgress?.current}/{generateAllProgress?.total})
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={handleGenerateAll}
                      disabled={isGenerating || isGeneratingAll}
                      className="gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      Generate All
                    </Button>
                  )
                )}
                {articleId && (
                  <Button
                    onClick={() => {
                      if (articleId) {
                        navigate(`/export/${articleId}?title=${encodeURIComponent(articleTitle || outline?.title || 'Article')}`);
                      }
                    }}
                    disabled={sections.length === 0 || !sections.every(s => s.status === 'complete')}
                    className="gap-2"
                    title={sections.every(s => s.status === 'complete') ? "Finalize article" : "Finish all sections first"}
                  >
                    <Download className="h-4 w-4" />
                    Finalize
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="w-full max-w-none p-6 space-y-6">

            {/* Article Header Image - Requirements: 1.1, 1.2, 1.3, 2.1, 3.1, 5.2 */}
            {(articleId || articleIdParam) && (
              <ArticleHeaderImage
                articleId={articleId || articleIdParam || ''}
                articleTitle={articleTitle || outline?.title || 'Article'}
                mode="article"
                onOpenWizard={() => handleOpenImageWizard(!!headerImageData)}
                imageData={headerImageData}
                isLoading={headerImageLoading || isImageGenerating}
                isGenerating={isImageGenerating}
                error={imageGenerationError}
                onRetry={handleRetryImageGeneration}
              />
            )}

            {/* Editable Article Title */}
            <div className="space-y-2">
              <Input
                value={articleTitle}
                onChange={(e) => setArticleTitle(e.target.value)}
                placeholder="Article Title"
                className="h-auto min-h-[3.5rem] text-3xl sm:text-4xl md:text-5xl font-bold border-none px-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-charcoal tracking-tight"
                style={{ fontFamily: 'var(--font-display)' }}
              />
            </div>

            {/* Fixed position saving indicator - no layout shift */}
            <div
              className={cn(
                "fixed bottom-4 right-4 z-50 transition-all duration-300",
                isSaving ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
              )}
            >
              <Badge variant="outline" className="shadow-lg bg-paper-warm border-ochre/30">
                <Loader2 className="mr-1 h-3 w-3 animate-spin text-burnt-sienna" />
                <span className="text-charcoal">Saving...</span>
              </Badge>
            </div>

            {error === 'no_outline' ? (
              <Card className="border-destructive">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="rounded-full bg-destructive/10 p-3">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">Structure First, Then Write</h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Build your article structure first, then come back here to start writing. Your structure will guide you through each section.
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate(`/outline/${articleIdParam}`)}
                      className="gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Build Your Structure
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-6">
              {sections.map((section) => (
                <Card
                  key={section.index}
                  ref={(el) => {
                    if (el) {
                      sectionRefs.current.set(section.index, el);
                    } else {
                      sectionRefs.current.delete(section.index);
                    }
                  }}
                  className={cn(
                    "paper-edge transition-all duration-300",
                    section.status === 'generating' && 'border-ochre ring-1 ring-ochre/20',
                    section.status === 'error' && 'border-destructive',
                    section.status === 'complete' && 'hover:shadow-md'
                  )}
                >
                  {section.status === 'error' && (
                    <div className="p-6 pb-0">
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Section Writing Failed</AlertTitle>
                        <AlertDescription className="mt-2">
                          <p className="mb-4">{section.errorMessage}</p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleRetry(section.index)}
                              variant="default"
                              size="sm"
                              className="gap-2"
                            >
                              <RefreshCw className="h-4 w-4" />
                              Retry
                            </Button>
                            <Button
                              onClick={handleManualEntry}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Edit className="h-4 w-4" />
                              Edit Your Plan
                            </Button>
                            <Button
                              onClick={() => handleSkip(section.index)}
                              variant="ghost"
                              size="sm"
                              className="gap-2"
                            >
                              <SkipForward className="h-4 w-4" />
                              Skip
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}

                  {!articleId && section.status === 'generating' && (
                    <div className="relative min-h-96">
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-paper-light/90 rounded-md z-10">
                        <div className="w-14 h-14 rounded-full bg-ochre/10 flex items-center justify-center mb-4">
                          <Loader2 className="h-8 w-8 text-burnt-sienna animate-spin" />
                        </div>
                        <p className="text-charcoal font-medium">Writing section...</p>
                        {section.progress !== undefined && (
                          <p className="text-sm text-muted-warm mt-2">
                            {Math.round(section.progress * 100)}%
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {articleId ? (
                    <ArticleSectionEditor
                      articleId={articleId}
                      section={{
                        index: section.index,
                        title: section.title,
                        content: section.content,
                        wordCount: section.wordCount,
                        metadata: section.metadata
                      } as ArticleSection}
                      sectionIndex={section.index}
                      onContentChange={(newContent) => {
                        const wordCount = calculateWordCount(newContent);
                        // Enable generation when content is deleted
                        const canGenerate = newContent.trim().length === 0;
                        updateSection(section.index, {
                          content: newContent,
                          wordCount,
                          canGenerate
                        });
                      }}
                      canRegenerate={
                        (section.content.length > 0 ||
                          (section.canGenerate &&
                            (section.index === 0 || sections.slice(0, section.index).every(s => s.content.length > 0))
                          )
                        ) && !isGenerating && !isSaving
                      }
                      onGenerate={handleGenerateSection}
                      isGenerating={isGenerating && generatingSectionIndex === section.index}
                      needsReview={section.needsReview}
                      streamingContent={streamingContent[section.index]}
                      isStreaming={isGenerating && generatingSectionIndex === section.index && section.index in streamingContent}
                    />
                  ) : (
                    <div className="p-6">
                      <div className="prose prose-sm max-w-none bg-muted p-4 rounded-md border min-h-96 font-serif">
                        <div className="whitespace-pre-wrap">{section.content}</div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}

              {/* References Section */}
              {loadedBrief?.urls && loadedBrief.urls.length > 0 && (
                <ArticleReferences sources={loadedBrief.urls} />
              )}

            </div>
          </div>
        </SidebarInset>

        {/* Navigation Guard */}
        <NavigationGuard
          shouldBlock={isGenerating || isSaving || isGeneratingAll}
          title="Writing in Progress"
          message={
            (() => {
              if (isGeneratingAll) return "All sections are being generated. Leaving now will cancel the process. Are you sure you want to leave?";
              if (isGenerating) return "Your section is being written. Leaving now will cancel the process. Are you sure you want to leave?";
              return "Your article is being saved. Leaving now may cause data loss. Are you sure you want to leave?";
            })()
          }
          onConfirm={() => {
            if (isGeneratingAll) {
              cancelGenerateAllRef.current = true;
            }
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
            }
          }}
        />

        {/* Deletion Confirmation Dialog */}
        <DeletionConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          title="Delete Article"
          itemName={articleTitle || 'this article'}
        />
      </div >
    </SidebarProvider >
  );
};
